import React from 'react';
// import ReactPullToRefresh from "react-pull-to-refresh";
import { API_URLS } from '../../../config/Constants';
import { sessionManager } from '../../../config/sessionManager';
import api from '../../../config/axiosConfig';
import Select, { SingleValue, StylesConfig } from 'react-select';
import TimeInput24 from './TimeInput24';
import { getHoursSinceGameStart } from '../../../utils/dateUtils';
import ShotLocationPicker, { PickedLocation } from '../../../components/shotChart/ShotLocationPicker';
import { LOCATABLE_SHOT_TYPES, SHOT_TYPE_TO_OUTCOME, ShotLocation, ShotOutcome, buildShotLocation } from '../../../utils/shotLocation';
export interface StatRow {
  _id?: string;
  time: string;
  desc: string;
  team: string;
  green?: boolean;
  shotLocation?: ShotLocation | null;
}

interface StatsListProps {
  statsData: StatRow[];
  onRefresh: () => Promise<void>;
  onEdit?: (row: StatRow, index: number) => void;
  onDelete?: (row: StatRow, index: number) => void;
  players: Array<{
    _id: string;
    no: string;
    firstName: string;
    lastName: string;
    position: string[];
  }>;
  statTypes: string[];
  onFilterChange: (filters: { playerId: string; statType: string }) => void;
  selectedPlayer: string;
  selectedStatType: string;
  period: string; // Add period prop
  isLoading?: boolean; // Add loading prop
  gameDate?: string; // Add game date prop for stats lock
  gameTime?: string; // Add game time prop for stats lock
  gameTimezone?: string; // Add game timezone prop for stats lock
  onAddStatClick: () => void; // Add onAddStatClick prop
}

// Define option types for react-select
interface OptionType {
  value: string;
  label: string;
}

// Move selectStyles definition above the component
const selectStyles: StylesConfig<OptionType, false> = {
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
  control: (provided) => ({
    ...provided,
    backgroundColor: '#05396b',
    color: 'white',
    borderColor: '#bccedf',
    minHeight: '32px',
    fontWeight: 'bold',
    fontSize: '0.875rem',
  }),
  singleValue: (provided) => ({ ...provided, color: 'white' }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#05396b' : state.isFocused ? '#1a4d7a' : '#05396b',
    color: 'white',
  }),
  dropdownIndicator: (provided) => ({ ...provided, color: 'white' }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: '#bccedf',
  }),
  input: (provided) => ({ ...provided, color: 'white' }),
};

// Portal-based select styles — menu renders outside the modal so it is never clipped
const modalSelectStyles: StylesConfig<OptionType, false> = {
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#fff',
    color: '#000',
  }),
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#fff',
    color: '#111827',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(59,130,246,0.3)' : 'none',
    minHeight: '38px',
    fontWeight: '500',
    fontSize: '16px',
    borderRadius: '8px',
  }),
  singleValue: (provided) => ({ ...provided, color: '#111827' }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#dbeafe' : state.isFocused ? '#f3f4f6' : '#fff',
    color: '#111827',
    fontSize: '16px',
    padding: '6px 8px',
  }),
  dropdownIndicator: (provided) => ({ ...provided, color: '#6b7280' }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: '#e5e7eb',
  }),
  input: (provided) => ({ ...provided, color: '#111827', fontSize: '16px' }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '16px',
    color: '#9ca3af',
  }),
};

const StatsList: React.FC<StatsListProps> = ({
  statsData,
  onRefresh,
  onEdit,
  onDelete,
  players,
  statTypes,
  onFilterChange,
  selectedPlayer,
  selectedStatType,
  period, // Destructure period
  gameDate, // Destructure game date
  gameTime, // Destructure game time
  gameTimezone, // Destructure game timezone
  onAddStatClick, // Destructure onAddStatClick
}) => {
  // Check stats edit and delete permissions
  const canEditStats = sessionManager.canPerformAction('games.gameMenu.stats', 'edit');
  const canDeleteStats = sessionManager.canPerformAction('games.gameMenu.stats', 'delete');

  // Check if user can bypass stats lock for past games (Head Coach or Employees)
  const canBypassStatsLock = sessionManager.canPerformAction('games.gameMenu.statsLockAfterTimeout', 'edit');

  // Check if game is in the past (more than 24 hours from start time) - timezone-aware
  const isGamePast = React.useMemo(() => {
    if (!gameDate) return false; // If no game date, assume not past

    try {
      const hoursSinceGame = getHoursSinceGameStart({
        date: gameDate,
        time: gameTime,
        timezone: gameTimezone,
      });

      return hoursSinceGame > 24;
    } catch (e) {
      console.error('Error checking if game is past:', e);
      return false;
    }
  }, [gameDate, gameTime, gameTimezone]);

  // Determine if stats can be edited (not locked)
  const canEditStatsNow = canEditStats && (!isGamePast || canBypassStatsLock);
  const canDeleteStatsNow = canDeleteStats && (!isGamePast || canBypassStatsLock);

  // Modal state
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [editRow, setEditRow] = React.useState<StatRow | null>(null);
  const [editForm, setEditForm] = React.useState({
    date: '',
    timeStr: '',
    statstype: '',
    playerId: '',
    player: 'us',
    possessionTime: '',
  });
  const [editShotLocation, setEditShotLocation] = React.useState<PickedLocation | null>(null);

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleteRow, setDeleteRow] = React.useState<StatRow | null>(null);

  const isMobile = (() => {
    // Check if device has touch capability
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Check if it's a mobile/tablet user agent (not a laptop with touchscreen)
    const mobileRegex =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS|Tablet|PlayBook|Silk|Kindle/i;
    const isMobileUA = mobileRegex.test(navigator.userAgent);

    // Only return true if BOTH touch-capable AND mobile user agent
    return hasTouch || isMobileUA;
  })();

  // Function to format team display with player name for US stats
  const formatTeamDisplay = (row: StatRow) => {
    // If it's a THEM stat, just return "THEM"
    if (row.team && row.team.toLowerCase().includes('them')) {
      // Check if it's a team stat for THEM (playerId: -2)
      const rowWithPlayerId = row as any;
      if (rowWithPlayerId.playerId && rowWithPlayerId.playerId === -2) {
        return 'THEM (Team Stat)';
      }
      return 'THEM';
    }

    // For US stats, try to find and display player information
    const rowWithPlayerId = row as any;
    if (rowWithPlayerId.playerId && rowWithPlayerId.playerId !== 'us') {
      // Check if it's a team stat for US (playerId: -2)
      if (rowWithPlayerId.playerId === -2) {
        return 'US (Team Stat)';
      }

      const player = players.find((p) => p._id === rowWithPlayerId.playerId);
      if (player) {
        return `US: (${player.no} ${player.firstName} ${player.lastName[0]}.)`;
      }
    }

    // Default to just "US" if no specific player found
    return 'US';
  };

  const sideOptions: OptionType[] = [
    { value: 'us', label: 'US' },
    { value: 'them', label: 'THEM' },
  ];

  // Keep edit modal type filtering aligned with Add Stat modal.
  const usOnlyTypes = new Set(['Faceoff Lost', 'Was Blocked By', 'Shot Was Saved By', 'Shot not saved']);

  const getTypeOptionsForSide = (side: string) => {
    if (!side) return [];
    if (side === 'us') return statTypes;
    return statTypes.filter((type) => !usOnlyTypes.has(type));
  };

  // Helper to determine if player dropdown should be shown and how to filter players
  const shouldShowPlayerDropdown = (statType: string, side: string) => {
    if (!statType || side !== 'us') return false;
    switch (statType) {
      case 'Penalty Start':
      case 'Penalty End':
      case 'Assist':
      case 'Shot Missed':
      case 'Shot Blocked':
      case 'Ground Ball':
      case 'Turnover':
      case 'Faceoff Won':
      case 'Faceoff Lost':
      case 'Forced Turnover':
      case 'Caused Turnover':
      case 'Shot Goal':
      case 'Shot Saved':
      case 'Shot not saved':
      case 'Shot Was Saved By':
      case 'Was Blocked By':
      case 'Shot Clock Violation':
        return true;
      default:
        return false;
    }
  };

  const getFilteredPlayers = (statType: string, side: string) => {
    if (side !== 'us') return [];
    switch (statType) {
      case 'Penalty Start':
      case 'Penalty End':
      case 'Assist':
      case 'Shot Missed':
      case 'Shot Blocked':
      case 'Ground Ball':
      case 'Turnover':
      case 'Shot Goal':
      case 'Faceoff Won':
      case 'Faceoff Lost':
      case 'Forced Turnover':
      case 'Caused Turnover':
      case 'Shot Saved':
      case 'Was Blocked By':
      case 'Shot Clock Violation':
        return players;
      case 'Shot not saved':
      case 'Shot Was Saved By':
        return players.filter(
          (p: any) => Array.isArray(p.position) && p.position.some((pos: string) => pos === 'Goalie'),
        );
      default:
        return players;
    }
  };

  // Open modal with row data
  const openEditModal = (row: StatRow, index: number) => {
    // If stat type is Possession X, split it
    let statstype = row.desc;
    let possessionTime = '';
    if (statstype.startsWith('Possession ')) {
      const parts = statstype.split(' ');
      statstype = parts[0];
      possessionTime = parts[1] || '';
    }
    // Determine player (us/them) from team field
    let side = 'us';
    if (row.team && row.team.toLowerCase().includes('them')) side = 'them';
    // Determine eligible players for this stat type and side
    const eligiblePlayers = getFilteredPlayers(statstype, side);
    // Set playerId to the stat log's playerId if it matches an eligible player
    let playerId = '';
    const playerIdFromRow = (row as any).playerId;
    if (playerIdFromRow && eligiblePlayers.some((p) => p._id === playerIdFromRow)) {
      playerId = playerIdFromRow;
    } else if (row.team && eligiblePlayers.some((p) => p._id === row.team)) {
      playerId = row.team;
    }
    // If playerId is still blank and player dropdown should show, set default to blank (so US/THEM is selected)
    const padTwo = (n: number) => String(n).padStart(2, '0');
    const rowDate = row.time ? new Date(row.time) : new Date();
    const dateStr = `${rowDate.getFullYear()}-${padTwo(rowDate.getMonth() + 1)}-${padTwo(rowDate.getDate())}`;
    const timeStr = `${padTwo(rowDate.getHours())}:${padTwo(rowDate.getMinutes())}:${padTwo(rowDate.getSeconds())}`;
    setEditRow(row);
    setEditForm({
      date: dateStr,
      timeStr: timeStr,
      statstype: statstype || '',
      playerId: playerId,
      player: side,
      possessionTime,
    });
    setEditShotLocation(row.shotLocation ? { x: row.shotLocation.x, y: row.shotLocation.y } : null);
    setEditModalOpen(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (row: StatRow, index: number) => {
    setDeleteRow(row);
    setDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (deleteRow && deleteRow._id) {
      try {
        await api.delete(API_URLS.DELETE_STATS, {
          data: { _id: deleteRow._id },
        });
        // Close modal and refresh data
        setDeleteModalOpen(false);
        setDeleteRow(null);
        if (onRefresh) await onRefresh();
      } catch (err) {
        console.error('Error deleting stat:', err);
        // Optionally show error message
      }
    }
  };
  // Handle form changes
  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle update (API call placeholder)
  const handleUpdate = async () => {
    // Get IDs from sessionManager
    const clientId = sessionManager.getClientId();
    const seasonId = sessionManager.getSeasonId();
    const gameId = sessionManager.getGameId();
    let type = editForm.statstype;
    let timeValue = undefined;
    if (type === 'Possession' && editForm.possessionTime) {
      type = `Possession ${editForm.possessionTime}`;
      timeValue = editForm.possessionTime;
    }
    const combinedTime =
      editForm.date && editForm.timeStr ? new Date(`${editForm.date}T${editForm.timeStr}`) : undefined;
    // Prepare payload
    const payload: any = {
      clientId,
      seasonId,
      gameId,
      type,
      player: editForm.player,
      period,
      time: combinedTime ? combinedTime.toISOString() : undefined,
    };
    if (editForm.player === 'us' && editForm.playerId) {
      payload.playerId = editForm.playerId;
    }
    if (editRow && editRow._id) payload._id = editRow._id;
    if (timeValue) payload.timeValue = timeValue;
    if ((editForm.player === 'us' || editForm.player === 'them') && LOCATABLE_SHOT_TYPES.includes(type)) {
      payload.shotLocation = editShotLocation ? buildShotLocation(editShotLocation.x, editShotLocation.y, 'postgame') : null;
    }
    // Call the update API
    try {
      await api.post(API_URLS.UPDATE_STATS, payload);
    } catch (err) {
      // Optionally show error
    }
    setEditModalOpen(false);
    setEditRow(null);
    setEditShotLocation(null);
    if (onRefresh) await onRefresh();
  };

  // Handle refresh with animation
  const handleRefreshClick = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      // Reset refreshing state after animation completes
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };

  return (
    <div className="w-full mt-8 bg-white rounded-xl shadow-lg relative overflow-visible">
      {/* Edit Modal */}
      {editModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
          onClick={() => setEditModalOpen(false)}
        >
          <div
            className="bg-white w-full sm:w-[480px] sm:max-w-[96vw] rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92dvh] overflow-y-auto px-5 pt-5 pb-8 flex flex-col gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle (mobile only) */}
            <div className="flex justify-center sm:hidden mb-1">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Edit Stat</h2>
              <button
                onClick={() => setEditModalOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            {/* Date + Time row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-gray-700">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={editForm.date}
                  onChange={handleEditFormChange}
                  className="border border-gray-300 rounded-lg text-base w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                  style={{ height: '38px' }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-gray-700">
                  Time <span className="text-red-500">*</span>
                </label>
                <TimeInput24
                  value={editForm.timeStr}
                  onChange={(val) => setEditForm((prev) => ({ ...prev, timeStr: val }))}
                  className="w-full"
                />
              </div>
            </div>

            {/* Side */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold text-gray-700">
                Side <span className="text-red-500">*</span>
              </label>
              <Select
                options={sideOptions}
                value={sideOptions.find((opt) => opt.value === editForm.player) || null}
                onChange={(option) => {
                  const newSide = option ? option.value : '';
                  setEditForm((prev) => ({
                    ...prev,
                    player: newSide,
                    statstype: '',
                    playerId: '',
                    possessionTime: '',
                  }));
                }}
                placeholder="Select Side"
                styles={modalSelectStyles as StylesConfig<OptionType, false>}
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            {/* Stat Type */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold text-gray-700">
                Stat Type <span className="text-red-500">*</span>
              </label>
              <Select
                options={getTypeOptionsForSide(editForm.player).map((type) => ({
                  value: type,
                  label: type,
                }))}
                value={
                  getTypeOptionsForSide(editForm.player)
                    .map((type) => ({ value: type, label: type }))
                    .find((opt) => opt.value === editForm.statstype) || null
                }
                onChange={(option) => {
                  const newStatType = option ? option.value : '';
                  setEditForm((prev) => ({
                    ...prev,
                    statstype: newStatType,
                    playerId: '',
                    possessionTime: newStatType === 'Possession' ? prev.possessionTime : '',
                  }));
                }}
                placeholder="Select Stat Type"
                isClearable
                styles={modalSelectStyles as StylesConfig<OptionType, false>}
                isDisabled={!editForm.player}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {editForm.statstype === 'Possession' && (
                <input
                  type="number"
                  step="0.1"
                  name="possessionTime"
                  className="border border-gray-300 rounded-lg px-3 text-base w-full mt-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  style={{ height: '38px' }}
                  placeholder="Enter Possession Time (seconds)"
                  value={editForm.possessionTime}
                  onChange={handleEditFormChange}
                />
              )}
            </div>

            {/* Shot Location — optional, shot-type stats for either side */}
            {(editForm.player === 'us' || editForm.player === 'them') &&
              LOCATABLE_SHOT_TYPES.includes(editForm.statstype) && (
              <div className="flex flex-col gap-1 border border-gray-200 rounded-lg p-3">
                <ShotLocationPicker
                  outcome={(SHOT_TYPE_TO_OUTCOME[editForm.statstype] as ShotOutcome) || null}
                  value={editShotLocation}
                  onChange={setEditShotLocation}
                  title="Shot location (optional)"
                />
              </div>
            )}

            {/* Player */}
            {shouldShowPlayerDropdown(editForm.statstype, editForm.player) && (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-gray-700">
                  Player <span className="text-red-500">*</span>
                </label>
                <Select
                  options={getFilteredPlayers(editForm.statstype, editForm.player).map((p) => ({
                    value: p._id,
                    label: `${p.no ? `#${p.no} ` : ''}${p.firstName} ${p.lastName}`,
                  }))}
                  value={
                    getFilteredPlayers(editForm.statstype, editForm.player)
                      .map((p) => ({
                        value: p._id,
                        label: `${p.no ? `#${p.no} ` : ''}${p.firstName} ${p.lastName}`,
                      }))
                      .find((opt) => opt.value === editForm.playerId) || null
                  }
                  onChange={(option) => {
                    setEditForm((prev) => ({
                      ...prev,
                      playerId: option ? option.value : '',
                    }));
                  }}
                  placeholder="Select Player"
                  isClearable
                  styles={modalSelectStyles as StylesConfig<OptionType, false>}
                  isDisabled={!editForm.statstype}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-2">
              <button
                className="flex-1 py-2 rounded-xl border border-gray-300 text-gray-700 font-semibold text-base hover:bg-gray-50 active:bg-gray-100 transition-colors"
                onClick={() => setEditModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="flex-1 py-2 rounded-xl bg-blue-600 text-white font-semibold text-base hover:bg-blue-700 active:bg-blue-800 transition-colors"
                onClick={handleUpdate}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg p-6 min-w-[320px] shadow-xl relative">
            <h2 className="text-lg font-bold mb-4 text-red-600">Delete Stat</h2>
            <div className="mb-4">
              <p className="text-gray-700">Are you sure you want to delete this stat?</p>
              <p className="text-sm text-gray-600 mt-2">
                <strong>Stat Type:</strong> {deleteRow?.desc}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Team:</strong> {deleteRow?.team}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Time:</strong>{' '}
                {deleteRow?.time
                  ? new Date(deleteRow.time).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: false,
                    })
                  : ''}
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-1 rounded bg-gray-200 hover:bg-gray-300"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setDeleteRow(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top Arrow Tab */}
      <div
        className="absolute left-1/2 -top-4 -translate-x-1/2 w-0 h-0 rotate-180 z-20
          border-l-[16px] border-l-transparent
          border-r-[16px] border-r-transparent
          border-t-[16px] border-t-white"
      />
      {/* Filter Controls - OUTSIDE of ReactPullToRefresh */}
      <div className="flex items-center justify-between px-4 pt-6 pb-2 z-50">
        <span className="font-bold text-lg">STATS</span>
        <div className="flex gap-2">
          {!isMobile && (
            <button
              type="button"
              className="bg-[#05396b] hover:bg-[#05396b]/80 text-white font-semibold text-base px-4 hidden sm:block"
              onClick={onAddStatClick}
            >
              Add Stat
            </button>
          )}
          <Select
            options={[
              { value: '', label: 'All Players' },
              ...players.map((p) => ({
                value: p._id,
                label: `${p.no} - ${p.firstName} ${p.lastName[0]}.`,
              })),
            ]}
            value={[
              { value: '', label: 'All Players' },
              ...players.map((p) => ({
                value: p._id,
                label: `${p.no} - ${p.firstName} ${p.lastName[0]}.`,
              })),
            ].find((opt) => opt.value === selectedPlayer)}
            onChange={(option: SingleValue<OptionType>) => {
              if (option)
                onFilterChange({
                  playerId: option.value,
                  statType: selectedStatType,
                });
            }}
            menuPlacement="top"
            styles={selectStyles as StylesConfig<OptionType, false>}
            isSearchable={false}
          />
          <Select
            options={[
              { value: '', label: 'All Stats' },
              ...statTypes.map((type) => ({
                value: type,
                label: type,
              })),
            ]}
            value={[
              { value: '', label: 'All Stats' },
              ...statTypes.map((type) => ({
                value: type,
                label: type,
              })),
            ].find((opt) => opt.value === selectedStatType)}
            onChange={(option: SingleValue<OptionType>) => {
              if (option)
                onFilterChange({
                  playerId: selectedPlayer,
                  statType: option.value,
                });
            }}
            menuPlacement="top"
            styles={selectStyles as StylesConfig<OptionType, false>}
            isSearchable={false}
          />
          <button type="button" className="ml-2" onClick={handleRefreshClick}>
            <img
              src="/icons/icon-refresh.png"
              alt="refresh"
              className={`w-5 h-5 ${isRefreshing ? 'opacity-50' : ''}`}
            />
          </button>
        </div>
      </div>
      {/* Only the stats list is wrapped by ReactPullToRefresh */}
      {/* <ReactPullToRefresh onRefresh={onRefresh}> */}
      <div className={`max-h-96 ${isRefreshing ? 'overflow-hidden' : 'overflow-y-auto'}`}>
        {/* Loading Spinner */}
        {isRefreshing && (
          <div role="status" className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        )}
        <div className={`${isRefreshing ? 'bg-white opacity-20 pointer-events-none' : ''}`}>
          {statsData?.length > 0 &&
            statsData?.map((row, i) => (
              <div
                key={i}
                className={`flex items-center px-3 py-1 text-sm border-b border-[#edf5fc] ${
                  i % 2 === 0 ? 'bg-[#eafbe3]' : 'bg-white'
                }`}
              >
                <div className="flex-shrink-0 pr-2">
                  {row?.time
                    ? new Date(row.time).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                      })
                    : ''}
                </div>
                <div className="flex-1 truncate">{row?.desc}</div>
                <div className="text-right pr-1 truncate">{formatTeamDisplay(row)}</div>
                {canEditStatsNow && (
                  <button
                    type="button"
                    className="mx-1"
                    onClick={() => openEditModal(row, i)}
                    title={
                      isGamePast && !canBypassStatsLock
                        ? 'Stats are locked for games that started more than 24 hours ago'
                        : 'Edit stat'
                    }
                  >
                    <img src="/icons/icon-edit.png" alt="edit" className="w-4 h-4" />
                  </button>
                )}
                {canDeleteStatsNow && (
                  <button
                    type="button"
                    className="mx-1"
                    onClick={() => openDeleteModal(row, i)}
                    title={
                      isGamePast && !canBypassStatsLock
                        ? 'Stats are locked for games that started more than 24 hours ago'
                        : 'Delete stat'
                    }
                  >
                    <img src="/icons/icon-delete.png" alt="delete" className="w-4 h-4" />
                  </button>
                )}
                {isGamePast && !canBypassStatsLock && (canEditStats || canDeleteStats) && (
                  <span
                    className="mx-1 text-gray-400 text-xs"
                    title="Stats locked - game started more than 24 hours ago"
                  >
                    🔒
                  </span>
                )}
              </div>
            ))}
        </div>
        {(!statsData || statsData.length === 0) && (
          <div className="flex justify-center items-center py-8 text-gray-500">No stats available for this period</div>
        )}
      </div>
      {/* </ReactPullToRefresh> */}
      <div className="flex flex-col justify-center items-center rounded-lg py-1">
        <img src="/icons/icon-table-down.png" alt="extend" className="w-4 h-4" />
      </div>
    </div>
  );
};

export default StatsList;
